import { getArticles, getNavbar, getPage } from '#api';
import { getNavbarSubpageHref } from '#components/Navbar/navbarLinks';

let searchIndexPromise;

const htmlToText = (value) => {
    if (typeof value !== 'string' || !value) return '';

    const parsedHtml = new DOMParser().parseFromString(value, 'text/html');
    parsedHtml.querySelectorAll('script, style, noscript').forEach((element) => element.remove());
    return parsedHtml.body.textContent.replace(/\s+/g, ' ').trim();
};

const collectFormText = (fields = []) =>
    Array.isArray(fields)
        ? fields.flatMap((field) => [
              htmlToText(field.label),
              htmlToText(field.placeholder),
              ...collectFormText(field.group),
          ])
        : [];

const getPageText = (elements = []) =>
    (Array.isArray(elements) ? elements : []).flatMap((element) => {
        switch (element.name) {
            case 'Title':
            case 'Paragraph':
            case 'Link':
                return [htmlToText(element.content)];
            case 'Form':
                return [htmlToText(element.title), ...collectFormText(element.content)];
            case 'TileGroup':
                return (element.content || []).flatMap((tile) => [
                    htmlToText(tile.title),
                    htmlToText(tile.panelContent),
                ]);
            default:
                return [];
        }
    }).filter(Boolean).join(' ');

const requireData = (response, fallbackMessage) => {
    if (response.error || response.data === null || response.data === undefined) {
        const detail =
            typeof response.error === 'string' ? response.error : response.error?.message;
        throw new Error(detail || fallbackMessage);
    }
    return response.data;
};

const loadSearchIndex = async () => {
    const [navbarResponse, articlesResponse] = await Promise.all([getNavbar(), getArticles()]);
    const navbar = requireData(navbarResponse, 'Nie udało się pobrać listy podstron.');
    const articles = requireData(articlesResponse, 'Nie udało się pobrać listy artykułów.');

    if (!Array.isArray(navbar) || !Array.isArray(articles)) {
        throw new Error('Nie udało się pobrać danych do wyszukiwania.');
    }

    const subpages = navbar.flatMap((page) =>
        (page.subpages || [])
            .filter((subpage) => subpage && subpage !== '__NONE__' && subpage.path)
            .map((subpage) => ({
                title: subpage.pl || subpage.name,
                href: getNavbarSubpageHref(page, subpage),
                location: `/${page.path}/${subpage.path}`,
                categoryPath: page.path,
                pagePath: subpage.path,
            })),
    );

    const pageResults = new Array(subpages.length);
    let nextSubpageIndex = 0;
    const workers = Array.from({ length: Math.min(6, subpages.length) }, async () => {
        while (nextSubpageIndex < subpages.length) {
            const index = nextSubpageIndex;
            nextSubpageIndex += 1;
            const subpage = subpages[index];
            const response = await getPage(subpage.categoryPath, subpage.pagePath);
            const elements = requireData(response, `Nie udało się pobrać podstrony: ${subpage.title}.`);

            pageResults[index] = {
                type: 'Podstrona',
                title: subpage.title,
                href: subpage.href,
                location: subpage.location,
                text: getPageText(elements),
            };
        }
    });
    await Promise.all(workers);

    const articleResults = articles.map((article) => ({
        type: 'Artykuł',
        title: article.title,
        href: `/artykul/${encodeURIComponent(article.title.replace(/\s+/g, '_'))}`,
        location: `/artykul/${encodeURIComponent(article.title.replace(/\s+/g, '_'))}`,
        text: htmlToText(article.content),
    }));

    return [...pageResults, ...articleResults];
};

export const getSearchIndex = () => {
    if (!searchIndexPromise) {
        searchIndexPromise = loadSearchIndex().catch((error) => {
            searchIndexPromise = undefined;
            throw error;
        });
    }
    return searchIndexPromise;
};

export const normalizeSearchText = (value) =>
    value
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase('pl-PL')
        .replace(/\s+/g, ' ')
        .trim();

const getMatchRange = (text, query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return null;

    let normalizedText = '';
    const sourceRanges = [];
    let offset = 0;
    let previousWasWhitespace = false;

    for (const character of text) {
        const start = offset;
        offset += character.length;

        if (/\s/u.test(character)) {
            if (previousWasWhitespace) continue;
            normalizedText += ' ';
            sourceRanges.push({ start, end: offset });
            previousWasWhitespace = true;
            continue;
        }

        previousWasWhitespace = false;
        const normalizedCharacter = character
            .normalize('NFD')
            .replace(/\p{Diacritic}/gu, '')
            .toLocaleLowerCase('pl-PL');

        for (const normalizedCodePoint of normalizedCharacter) {
            normalizedText += normalizedCodePoint;
            for (let index = 0; index < normalizedCodePoint.length; index += 1) {
                sourceRanges.push({ start, end: offset });
            }
        }
    }

    const matchIndex = normalizedText.indexOf(normalizedQuery);
    if (matchIndex < 0) return null;

    const lastMatchedRange = sourceRanges[matchIndex + normalizedQuery.length - 1];
    return {
        start: sourceRanges[matchIndex].start,
        end: lastMatchedRange.end,
    };
};

export const getHighlightedText = (text, query) => {
    const range = getMatchRange(text, query);
    if (!range) return [{ text, matched: false }];

    return [
        { text: text.slice(0, range.start), matched: false },
        { text: text.slice(range.start, range.end), matched: true },
        { text: text.slice(range.end), matched: false },
    ].filter((part) => part.text);
};

export const searchIndex = (index, query) => {
    const normalizedQuery = normalizeSearchText(query);
    if (!normalizedQuery) return [];

    const getRank = (title) => {
        if (title === normalizedQuery) return 0;
        if (title.startsWith(normalizedQuery)) return 1;
        if (title.includes(normalizedQuery)) return 2;
        return 3;
    };

    return index
        .map((item, position) => ({ item, position }))
        .filter(({ item }) =>
            normalizeSearchText(`${item.title} ${item.text}`).includes(normalizedQuery),
        )
        .sort((first, second) => {
            const firstRank = getRank(normalizeSearchText(first.item.title));
            const secondRank = getRank(normalizeSearchText(second.item.title));
            return firstRank - secondRank || first.position - second.position;
        })
        .map(({ item }) => item);
};

export const getSearchExcerpt = (text, query, fromStart = false) => {
    if (fromStart) {
        const excerpt = text.slice(0, 360).trim();
        return `${excerpt}${text.length > 360 ? '…' : ''}`;
    }

    const matchRange = getMatchRange(text, query);
    if (!matchRange) return '';

    const start = Math.max(0, matchRange.start - 60);
    const end = Math.min(text.length, Math.max(start + 360, matchRange.end + 60));
    const excerpt = text.slice(start, end).trim();

    return `${start > 0 ? '…' : ''}${excerpt}${end < text.length ? '…' : ''}`;
};
